import type {TabState} from './workspace-context';
import {normalizeAttachments} from './conversation-attachments';
import {pendingComposerAfterAcceptance,type ComposerDraft,type ComposerAcceptanceOptions} from './pending-composer';
import {annotationKey,type AnnotationCommentRequest} from './response-annotations';
import type {ResponseAnnotation} from '@reados/contracts';

/** Replace a draft's address without opening, focusing or moving its surface. */
export function promoteConversationTab(tabs:TabState,pendingId:string,threadId:string):TabState {
 if(!tabs.ids.includes(pendingId))return tabs;
 const ids=[...new Set(tabs.ids.map(id=>id===pendingId?threadId:id))];
 const active=tabs.active===pendingId?threadId:tabs.active;
 const secondary=tabs.secondary===pendingId?threadId:tabs.secondary;
 return {...tabs,ids,active,secondary:secondary===active?null:secondary};
}

/** A server id changes routing/storage, never the identity of a mounted Chat. */
export function retainConversationSurface(surfaces:Map<string,string>,pendingId:string,threadId:string):void {
 surfaces.set(threadId,surfaces.get(pendingId)??pendingId);
}

/** Transfer only composer-owned state; server receipts and draft setup metadata
 * stay in the pending namespace. Chat adopts this snapshot before persisting. */
export function transferPendingComposerState(storage:Pick<Storage,'getItem'|'setItem'>,from:string,to:string,submitted:ComposerDraft,options:ComposerAcceptanceOptions={}):ComposerDraft {
 const read=<T,>(key:string,fallback:T):T=>{try{const raw=storage.getItem(key);return raw?JSON.parse(raw):fallback;}catch{return fallback;}};
 const write=(key:string,value:unknown)=>{try{storage.setItem(key,JSON.stringify(value));}catch{}};
 const current={draft:read(`${from}.draft`,''),annotations:read<ResponseAnnotation[]>(`${from}.annotations`,[]),attachments:normalizeAttachments(read(`${from}.attachments`,[]))};
 const remaining=pendingComposerAfterAcceptance(current,submitted,options);
 write(to,read(from,{}));
 write(`${to}.draft`,remaining.draft);write(`${to}.annotations`,remaining.annotations);write(`${to}.attachments`,remaining.attachments??[]);
 for(const suffix of ['planMode','goalMode','verbosity','usage']){const value=read<unknown>(`${from}.${suffix}`,null);if(value!==null)write(`${to}.${suffix}`,value);}
 const request=read<AnnotationCommentRequest|null>(`${from}.annotationComment`,null);
 write(`${to}.annotationComment`,request&&remaining.annotations.some(item=>annotationKey(item)===request.key)?request:null);
 return remaining;
}
